// src/components/admin/Dashboard.tsx
import React from "react";
import {
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

/* ===== 型 ===== */
type RawOrder =
  | {
      id?: string | number;
      type?: string; // "charge" | "plan" | "point_purchase" など
      planId?: number;
      planName?: string;
      buyer?: string;
      userName?: string;
      email?: string;
      amount?: number;
      amountJPY?: number;
      priceJPY?: number;
      pointSpent?: number;
      date?: string; // ISO
      purchasedAt?: string; // ISO
      createdAt?: string; // ISO
      userId?: number;
      affiliateId?: string | null;
      affiliateName?: string | null;
    }
  | Record<string, any>;

type ChargeRow = {
  id: string;
  userId?: number;
  buyer: string;
  amountJPY: number; // 実際に課金された金額（円）
  chargedAt: string; // ISO
};

type PlanRow = {
  id: string;
  userId?: number;
  planId?: number;
  planName: string;
  amountJPY: number; // このコンポーネント内だけで使う「消費ポイント(pt)」
  pointSpent: number;
  purchasedAt: string; // ISO
};

type UserRow = {
  id: number;
  createdAt?: string;
};

/* ===== ユーティリティ ===== */
function jpy(n: number) {
  return (Number(n) || 0).toLocaleString("ja-JP");
}
function ym(d: string) {
  // 日本時間（ブラウザのローカルタイム）ベースで年月を切り出し
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = dt.getMonth() + 1;
  return `${y}-${String(m).padStart(2, "0")}`;
}
function nowYM() {
  const dt = new Date();
  const y = dt.getFullYear();
  const m = dt.getMonth() + 1;
  return `${y}-${String(m).padStart(2, "0")}`;
}
function lastNMonths(n: number) {
  const base = new Date();
  base.setDate(1);
  const list: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(base);
    d.setMonth(base.getMonth() - i);
    list.push(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
    );
  }
  return list;
}
function ymToLabel(ymStr: string) {
  return `${ymStr.slice(5, 7)}月`;
}

/** API応答の形を問わず [RawOrder] に正規化 */
function normalizeOrders(input: any): RawOrder[] {
  let arr: any[] = [];
  if (Array.isArray(input?.items)) arr = input.items;
  else if (Array.isArray(input)) arr = input;
  else arr = [];

  // 二重配列 [[...]] 対応
  if (arr.length === 1 && Array.isArray(arr[0])) arr = arr[0];

  return arr.map((r: any, idx: number) => ({
    id: r.id ?? r.orderId ?? idx + 1,
    type: r.type,
    planId: r.planId,
    planName: r.planName ?? r.plan ?? "未分類",
    buyer: r.buyer ?? r.userName ?? "",
    userName: r.userName,
    email: r.email,
    amount: r.amount,
    amountJPY: r.amountJPY,
    priceJPY: r.priceJPY,
    pointSpent: r.pointSpent,
    date: r.date ?? r.purchasedAt ?? r.createdAt,
    purchasedAt: r.purchasedAt,
    createdAt: r.createdAt,
    userId: r.userId,
    affiliateId: r.affiliateId ?? null,
    affiliateName: r.affiliateName ?? null,
  }));
}

// 「これは課金レコードか？」判定
function isCharge(r: RawOrder): boolean {
  const any = r as any;

  const planName = (any.planName || any.plan || "") as string;
  const point = Number(any.pointSpent ?? 0);

  // ① 「ポイント購入／ポイントチャージ」系の名前はすべて課金扱い
  const isPointChargeName =
    typeof planName === "string" &&
    (planName.startsWith("ポイント購入") ||
      planName.startsWith("ポイントチャージ"));

  if (isPointChargeName) {
    return true;
  }

  // ② type が文字列で入っている場合はそれを優先
  if (
    any.type === "charge" ||
    any.type === "point_purchase" ||
    any.type === "point_charge"
  ) {
    return true;
  }
  if (any.type === "plan") return false;

  const planId = any.planId;

  const hasPoint = Number.isFinite(point) && point > 0;
  // ③ 「ポイント購入/チャージ」は上で処理済みなので、ここでは通常プランだけを判定
  const hasPlanInfo =
    planId != null ||
    (planName &&
      !planName.startsWith("ポイント購入") &&
      !planName.startsWith("ポイントチャージ"));

  // プラン情報 or ポイント消費があれば「プラン側」
  if (hasPoint || hasPlanInfo) return false;

  // 金額だけ立っていれば「課金」
  const amount = Number(any.priceJPY ?? any.amountJPY ?? any.amount ?? 0);
  return Number.isFinite(amount) && amount > 0;
}

function isPlan(r: RawOrder): boolean {
  return !isCharge(r);
}

// RawOrder -> 課金行（実売上）
function mapCharge(r: RawOrder, idx: number): ChargeRow {
  const any = r as any;
  const idRaw = any.id ?? any.orderId ?? `charge-${idx + 1}`;
  const id = String(idRaw);

  const buyer = any.buyer || any.userName || "—";
  const amountJPY =
    Number(any.priceJPY ?? any.amountJPY ?? any.amount ?? 0) || 0;
  const chargedAt: string =
    any.date || any.purchasedAt || any.createdAt || new Date().toISOString();

  return {
    id,
    userId: any.userId,
    buyer,
    amountJPY,
    chargedAt,
  };
}

// RawOrder -> プラン行（消費ポイント）
function mapPlan(r: RawOrder, idx: number): PlanRow {
  const any = r as any;
  const idRaw = any.id ?? any.orderId ?? `plan-${idx + 1}`;
  const id = String(idRaw);

  const planName = any.planName ?? any.plan ?? "未分類";

  // pointSpent が無い旧レコードは amount を pt として扱う（表示用の消費pt）
  const pointSpent = Number(any.pointSpent ?? any.amount ?? 0) || 0;
  const amountJPY = pointSpent; // 「円」ではなく pt 数。名前だけそのまま流用。

  const purchasedAt: string =
    any.purchasedAt || any.date || any.createdAt || new Date().toISOString();

  return {
    id,
    userId: any.userId,
    planId: any.planId,
    planName,
    amountJPY,
    pointSpent,
    purchasedAt,
  };
}

/* ===== 本体 ===== */
const Dashboard: React.FC = () => {
  const [orders, setOrders] = React.useState<RawOrder[]>([]);
  const [users, setUsers] = React.useState<UserRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr("");

        const [oRes, uRes] = await Promise.all([
          fetch("/api/admin/orders/history", {
            credentials: "include",
            cache: "no-store",
          }),
          fetch("/api/admin/users", {
            credentials: "include",
            cache: "no-store",
          }),
        ]);

        const [oText, uText] = await Promise.all([oRes.text(), uRes.text()]);

        let oJson: any = {};
        let uJson: any = [];
        try {
          oJson = JSON.parse(oText);
        } catch {
          console.warn("orders non-JSON:", oText.slice(0, 120));
        }
        try {
          uJson = JSON.parse(uText);
        } catch {
          console.warn("users  non-JSON:", uText.slice(0, 120));
        }

        setOrders(normalizeOrders(oJson));
        setUsers(Array.isArray(uJson) ? uJson : []);
      } catch (e: any) {
        setErr(e?.message || "データ取得に失敗しました。");
        setOrders([]);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ==== 課金 / プラン に分解 ====
  const chargeRows = React.useMemo<ChargeRow[]>(
    () => orders.filter(isCharge).map(mapCharge),
    [orders]
  );
  const planRows = React.useMemo<PlanRow[]>(
    () => orders.filter(isPlan).map(mapPlan),
    [orders]
  );

  /* === サマリー === */
  // ★ 今月の課金額だけ（これで「1月課金なし」なら0になる）
  const totalRevenueThisMonth = React.useMemo(() => {
    const curYM = nowYM();
    return chargeRows
      .filter((r) => ym(r.chargedAt) === curYM)
      .reduce((s, r) => s + (Number(r.amountJPY) || 0), 0);
  }, [chargeRows]);

  // 販売されたプラン = プラン側レコード数（全期間の総数のまま）
  const soldPlansTotal = planRows.length;

  const thisMonthUsers = React.useMemo(() => {
    if (!users?.length) return 0;
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    return users.filter((u) => {
      if (!u.createdAt) return false;
      const d = new Date(u.createdAt);
      return d.getFullYear() === y && d.getMonth() === m;
    }).length;
  }, [users]);

  const totalUsers = users.length;

  /* === プラン別内訳（消費ポイント） === */
  const planAgg = React.useMemo(() => {
    const map = new Map<string, { name: string; count: number; sales: number }>();
    for (const r of planRows) {
      const key = r.planName || "未分類";
      if (!map.has(key)) map.set(key, { name: key, count: 0, sales: 0 });
      const o = map.get(key)!;
      o.count += 1;
      o.sales += Number(r.amountJPY) || 0; // 「pt数」を積み上げ
    }
    return Array.from(map.values()).sort((a, b) => b.sales - a.sales);
  }, [planRows]);

  /* === 月別グラフ（直近6か月：プラン別消費pt + 課金額） === */
  const monthlyChart = React.useMemo(() => {
    const months = lastNMonths(6);
    const top3 = planAgg.slice(0, 3).map((p) => p.name);

    const byMonthPlan = new Map<string, Map<string, number>>();
    const byMonthCharge = new Map<string, number>();

    // プラン消費（pt）…棒グラフ用
    for (const r of planRows) {
      const k = ym(r.purchasedAt);
      if (!k || !months.includes(k)) continue;
      const pm = byMonthPlan.get(k) || new Map<string, number>();
      byMonthPlan.set(k, pm);
      const name = r.planName || "未分類";
      pm.set(name, (pm.get(name) || 0) + (Number(r.amountJPY) || 0));
    }

    // 課金額（円）…折れ線用
    for (const c of chargeRows) {
      const k = ym(c.chargedAt);
      if (!k || !months.includes(k)) continue;
      byMonthCharge.set(
        k,
        (byMonthCharge.get(k) || 0) + (Number(c.amountJPY) || 0)
      );
    }

    const rows = months.map((m) => {
      const pm = byMonthPlan.get(m) || new Map<string, number>();
      const row: any = { month: ymToLabel(m) };
      for (const name of top3) row[name] = pm.get(name) || 0;
      row.chargeTotal = byMonthCharge.get(m) || 0; // 各月の課金額（円）
      return row;
    });

    return { rows, top3 };
  }, [planRows, chargeRows, planAgg]);

  /* === UI === */
  if (loading) return <div style={{ padding: 24 }}>読み込み中…</div>;

  const stats = [
    { label: "今月の収益（課金額）", value: jpy(totalRevenueThisMonth) },
    { label: "販売されたプランの総数", value: soldPlansTotal.toLocaleString() },
    { label: "今月のユーザー", value: thisMonthUsers.toLocaleString() },
    { label: "全ユーザー数", value: totalUsers.toLocaleString() },
  ];

  return (
    <div
      style={{
        maxWidth: 1200,
        margin: "40px auto",
        background: "#fff",
        borderRadius: 8,
        padding: 24,
        boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
      }}
    >
      <h2 style={{ fontSize: "1.5rem", marginBottom: 24 }}>ダッシュボード</h2>

      {/* サマリー */}
      <div
        style={{
          display: "flex",
          gap: 32,
          marginBottom: 36,
          flexWrap: "wrap",
        }}
      >
        {stats.map((item) => (
          <div
            key={item.label}
            style={{
              background: "#6c7680",
              color: "#fff",
              borderRadius: 8,
              padding: "32px 24px 20px",
              flex: 1,
              minWidth: 220,
              fontSize: 28,
              fontWeight: "bold",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div>{item.value}</div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 400,
                marginTop: 24,
                color: "#fff",
              }}
            >
              {item.label}
            </div>
          </div>
        ))}
      </div>

      {/* 内訳テーブル（プラン側は消費ポイント） */}
      <h3 style={{ margin: "40px 0 16px", fontSize: "1.2rem" }}>
        販売プラン内訳
      </h3>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          background: "#f9fbfc",
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        <thead>
          <tr style={{ background: "#e6e9ee", fontWeight: "bold" }}>
            <th style={{ padding: "10px 8px", textAlign: "left" }}>プラン名</th>
            <th style={{ padding: "10px 8px" }}>販売数</th>
            <th style={{ padding: "10px 8px" }}>消費ポイント（pt）</th>
          </tr>
        </thead>
        <tbody>
          {planAgg.map((p) => (
            <tr key={p.name}>
              <td style={{ padding: "10px 8px" }}>{p.name}</td>
              <td style={{ padding: "10px 8px", textAlign: "center" }}>
                {p.count}
              </td>
              <td style={{ padding: "10px 8px", textAlign: "right" }}>
                {p.sales.toLocaleString()}pt
              </td>
            </tr>
          ))}
          {!planAgg.length && (
            <tr>
              <td
                colSpan={3}
                style={{ padding: "10px 8px", textAlign: "center" }}
              >
                データがありません
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* 月次グラフ：プラン別消費pt + 課金額（円） */}
      <div style={{ marginTop: 40 }}>
        <h3>📊 月別グラフ（プラン別消費pt ＋ 課金額）</h3>

        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={monthlyChart.rows}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip
              formatter={(value: any, name: string) => {
                const num = Number(value) || 0;
                if (name === "課金額合計（円）") {
                  return [jpy(num) + "円", name];
                }
                return [num.toLocaleString("ja-JP") + "pt", name];
              }}
            />
            <Legend />

            {/* プラン別ポイント消費（棒グラフ） */}
            {monthlyChart.top3.map((name, idx) => (
              <Bar
                key={name}
                dataKey={name}
                stackId="a"
                fill={["#8884d8", "#82ca9d", "#ffc658"][idx % 3]}
              />
            ))}

            {/* 課金額（円）を折れ線で表示 */}
            <Line
              type="monotone"
              dataKey="chargeTotal"
              stroke="#ff4c4c"
              name="課金額合計（円）"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {err && (
        <div style={{ marginTop: 12, color: "crimson" }}>
          ※警告: {err}（UIは暫定表示中）
        </div>
      )}
    </div>
  );
};

export default Dashboard;
